import { isBrowserOffline } from "./retryPolicy";
import { UploadCancelledError } from "./uploadChunk";
import { UPLOAD_DRAIN_POLL_INTERVAL_MS } from "./periodicWake";

export interface UploadConnectivityPort {
	isOnline(): boolean;
	waitForOnline(cancelSignal?: AbortSignal): Promise<void>;
}

/** How often a park re-reads connectivity, for the transitions no event ever tells us about. */
export const ONLINE_RECHECK_INTERVAL_MS = 250;

export type OnlineWaitOutcome = "online" | "released";

export interface BrowserOnlineWait {
	/** Settles "online" once connectivity is observed, or "released" when the caller gives up. Never rejects. */
	readonly settled: Promise<OnlineWaitOutcome>;
	/** Detaches every listener and the recheck timer. Idempotent; settles "released" if still pending. */
	release(): void;
}

/**
 * LSP-19334: park until the device is observably online again.
 *
 * The naive version of this waits on the `online` event alone, and that is a permanent hang waiting
 * to happen. `online` fires ONCE, at the moment of the transition. iOS Safari suspends a backgrounded
 * tab, so a wifi -> cellular handoff that completes while the page is frozen produces nothing the page
 * can observe on resume — yet `navigator.onLine` reads true again. A bfcache restore lands the same
 * way. With no re-read and no timeout, the upload never resumes: the customer's wheel sits at whatever
 * percentage it reached while the page insists it is offline, and because a parked foreground run
 * still holds its sessions in `markFileUploadSessionActive`, the background drain is filtered out of
 * rescuing them.
 *
 * So the event is treated as an optimisation, not the contract. Current state is re-read on a short
 * interval, and additionally on the wakes a resumed tab really does receive (`pageshow`,
 * `visibilitychange`) so returning to the tab does not cost a recheck tick.
 *
 * The two kinds of signal are handled differently on purpose. `online` is the browser ASSERTING
 * connectivity, so it is taken at its word — if it were somehow wrong the next PUT fails with a
 * TypeError and `uploadChunk` parks again, which is self-correcting. `pageshow` and
 * `visibilitychange` assert nothing about the network; they only mean "you may have missed
 * something", so they are a prompt to re-read and never release the park on their own.
 *
 * `maxWaitMs` adds LSP-19396's unconditional escape on top, for the case re-reading structurally
 * cannot solve: a `navigator.onLine` that is stuck false after the network is back, where every
 * recheck reads the same lie and no `online` event is coming. Callers whose retry is cheap
 * (`createBrowserConnectivityPort`, feeding `uploadChunk`'s no-attempt-spent offline branch) pass a
 * budget and accept a wasted PUT; callers driving customer-visible state (OptionsPage's offline
 * banner) omit it and wait for connectivity they can actually observe.
 */
export function waitForBrowserOnline(options: { releaseWhen?: () => boolean; maxWaitMs?: number } = {}): BrowserOnlineWait {
	if (!isBrowserOffline()) return { settled: Promise.resolve<OnlineWaitOutcome>("online"), release: () => {} };

	let settle!: (outcome: OnlineWaitOutcome) => void;
	const settled = new Promise<OnlineWaitOutcome>((resolve) => {
		settle = resolve;
	});
	let recheckTimer: ReturnType<typeof setInterval> | null = null;
	let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
	let hasSettled = false;
	// The `online` listener is registered `{ once: true }`, so once it has fired it has already removed
	// itself and re-removing it would be noise. Tracked rather than removed unconditionally because
	// callers assert that a park resolved BY the event leaves no removal behind (see
	// browserConnectivityPort.spec.ts "does not accumulate online listeners across repeated waits").
	let onlineListenerFired = false;

	const finish = (outcome: OnlineWaitOutcome, reason: string, trustedSignal = false): void => {
		if (hasSettled) return;
		hasSettled = true;
		if (recheckTimer !== null) {
			clearInterval(recheckTimer);
			recheckTimer = null;
		}
		if (maxWaitTimer !== null) {
			clearTimeout(maxWaitTimer);
			maxWaitTimer = null;
		}
		if (!onlineListenerFired) window.removeEventListener("online", onOnlineEvent);
		window.removeEventListener("pageshow", onLifecycleWake);
		document.removeEventListener("visibilitychange", onLifecycleWake);
		// CLAUDE.md -> "Diagnostic logging when fixing silent failures". A park that never ends is
		// indistinguishable in Datadog from one that ended fine, which is precisely why this class of
		// hang costs hours to diagnose. One line in, one line out, with the reason that released it.
		//
		// `trustedSignal` is reported because `online` is taken at its word: that path can legitimately
		// settle while `navigator.onLine` still reads false, and without this flag the line reads as a
		// self-contradiction (`outcome: "online", online: false`) to whoever is triaging it.
		console.warn(`[Upload] connectivity park ended: ${reason}`, { outcome, trustedSignal, online: !isBrowserOffline() });
		settle(outcome);
	};

	const recheck = (reason: string, trustSignal = false): void => {
		// A superseded run outranks connectivity: it must not resume just because the network did.
		if (options.releaseWhen?.()) {
			finish("released", `${reason}-caller-released`);
			return;
		}
		if (trustSignal || !isBrowserOffline()) finish("online", reason, trustSignal);
	};

	// Two handlers, because the two kinds of signal mean different things (see the doc comment).
	// `online` is the browser asserting connectivity and settles the park directly; a lifecycle wake
	// only earns a re-read of the flag.
	const onOnlineEvent = (): void => {
		onlineListenerFired = true;
		recheck("online", true);
	};
	const onLifecycleWake = (event: Event): void => {
		// `visibilitychange` fires on the way out as well as the way back; only a return to the
		// foreground says anything new about a transition we may have slept through.
		if (event.type === "visibilitychange" && document.visibilityState !== "visible") return;
		recheck(event.type);
	};

	window.addEventListener("online", onOnlineEvent, { once: true });
	window.addEventListener("pageshow", onLifecycleWake);
	document.addEventListener("visibilitychange", onLifecycleWake);
	recheckTimer = setInterval(() => recheck("recheck"), ONLINE_RECHECK_INTERVAL_MS);
	if (options.maxWaitMs !== undefined) {
		// LSP-19396's backstop, kept because it covers a case the recheck above structurally cannot:
		// re-reading `navigator.onLine` is useless when the flag itself is stuck false (Safari after
		// sleep, some WebViews), and no `online` event is coming either. Resolving blind after the
		// budget costs the caller one failed PUT; not resolving costs the upload.
		maxWaitTimer = setTimeout(() => {
			if (options.releaseWhen?.()) {
				finish("released", "max-wait-caller-released");
				return;
			}
			finish("online", "max-wait", true);
		}, options.maxWaitMs);
	}
	console.warn("[Upload] connectivity park started — the device reports offline; waiting for it to return");

	return { settled, release: () => finish("released", "release") };
}

/**
 * The browser-backed connectivity port every chunked-upload host wires into `ports.connectivity`.
 *
 * `uploadChunk` only consults the port when a chunk PUT fails with a TypeError while the device
 * reports itself offline, and its offline branch `continue`s WITHOUT advancing `attempt`. With no
 * port wired, an offline device instead burns all of its per-chunk retries (each with its own
 * backoff) and throws outward before the event-driven resume can do anything — behaviourally
 * survivable, but wasteful and noisy, and it turns a laptop-lid-close into a needs-attention-shaped
 * log storm.
 *
 * `waitForOnline` also resolves on its own after `OFFLINE_REPROBE_INTERVAL_MS`, whether or not an
 * `online` event arrived. `navigator.onLine` can stay false after the network is back (Safari after
 * sleep, some WebViews) and then no `online` event ever fires; the caller's offline branch retries
 * without spending an attempt, so a spurious wake costs one failed PUT per poll and a missed one
 * costs the upload.
 *
 * `waitForOnline` rejects with `UploadCancelledError` when the signal aborts. Hosts that run with
 * `cancelEnabled: false` still pass an abort signal for teardown, and a wait that ignored it would
 * keep listeners alive on a page that is going away — so everything is detached on whichever side
 * settles first.
 */
export const OFFLINE_REPROBE_INTERVAL_MS = UPLOAD_DRAIN_POLL_INTERVAL_MS;

export function createBrowserConnectivityPort(): UploadConnectivityPort {
	return {
		isOnline: () => !isBrowserOffline(),
		waitForOnline: (cancelSignal?: AbortSignal) => {
			// Break-it round 1: the abort check has to precede the fast online path. A caller that tore
			// down while the flag happened to read online otherwise got a resolved wait and marched on
			// into a PUT for an upload that was already cancelled.
			if (cancelSignal?.aborted) return Promise.reject(new UploadCancelledError("Upload cancelled while waiting for network"));
			if (!isBrowserOffline()) return Promise.resolve();
			// Both fixes for this hang are kept because they cover different failures: the shared park
			// re-reads state and reacts to `pageshow`/`visibilitychange`, which resumes in milliseconds
			// when the flag tells the truth; OFFLINE_REPROBE_INTERVAL_MS bounds the wait for when it
			// never does.
			const wait = waitForBrowserOnline({ maxWaitMs: OFFLINE_REPROBE_INTERVAL_MS });
			const onCancel = () => wait.release();
			cancelSignal?.addEventListener("abort", onCancel, { once: true });
			return wait.settled.then((outcome) => {
				cancelSignal?.removeEventListener("abort", onCancel);
				if (outcome === "released") throw new UploadCancelledError("Upload cancelled while waiting for network");
			});
		}
	};
}
